import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { GraduationCap, CheckCircle2, XCircle, Sparkles, Award, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

const DONE_KEY = "pod_modules_done_v2";
const QUIZ_KEY = "pod_module_quiz_v2";
const TEACH_KEY = "pod_module_teach_v2";

function loadJSON(k, fallback) {
  try { return JSON.parse(localStorage.getItem(k) || "null") ?? fallback; } catch { return fallback; }
}
function saveJSON(k, v) { localStorage.setItem(k, JSON.stringify(v)); }

export default function Learn() {
  const { user } = useAuth();
  const { lang, t } = useLang();
  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(true);
  const [done, setDone] = useState(() => loadJSON(DONE_KEY, {}));
  const [quiz, setQuiz] = useState(() => loadJSON(QUIZ_KEY, {})); // { m1: { 0: 2, 1: 0 } }
  const [teach, setTeach] = useState(() => loadJSON(TEACH_KEY, {}));
  const [open, setOpen] = useState(null);

  useEffect(() => {
    setLoadingItems(true);
    api.get(`/modules?lang=${lang}`)
      .then((r) => setItems(r.data.items || []))
      .finally(() => setLoadingItems(false));
  }, [lang]);

  // Cloud sync when logged in — merge (don't clobber local progress)
  useEffect(() => {
    if (!user || typeof user !== "object") return;
    (async () => {
      try {
        const { data } = await api.get("/learn/state");
        const cloudHasData =
          (data.done && Object.keys(data.done).length) ||
          (data.quiz && Object.keys(data.quiz).length) ||
          (data.teach && Object.keys(data.teach).length);
        if (cloudHasData) {
          // merge: cloud wins on conflicting keys, local fills gaps
          const mergedDone = { ...done, ...(data.done || {}) };
          const mergedQuiz = { ...quiz, ...(data.quiz || {}) };
          const mergedTeach = { ...teach, ...(data.teach || {}) };
          setDone(mergedDone); saveJSON(DONE_KEY, mergedDone);
          setQuiz(mergedQuiz); saveJSON(QUIZ_KEY, mergedQuiz);
          setTeach(mergedTeach); saveJSON(TEACH_KEY, mergedTeach);
          api.put("/learn/state", { done: mergedDone, quiz: mergedQuiz, teach: mergedTeach }).catch((err) => console.warn("learn cloud sync failed", err));
        } else {
          // cloud empty — push local up so signing in never loses work
          api.put("/learn/state", { done, quiz, teach }).catch((err) => console.warn("learn cloud sync failed", err));
        }
      } catch (err) {
        console.warn("learn state pull failed", err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const persist = (nextDone, nextQuiz, nextTeach) => {
    saveJSON(DONE_KEY, nextDone);
    saveJSON(QUIZ_KEY, nextQuiz);
    saveJSON(TEACH_KEY, nextTeach);
    if (user && typeof user === "object") {
      api.put("/learn/state", { done: nextDone, quiz: nextQuiz, teach: nextTeach })
        .catch((err) => console.warn("learn cloud sync failed", err));
    }
  };

  const answerQuiz = (mid, qIdx, choice) => {
    const nextQuiz = { ...quiz, [mid]: { ...(quiz[mid] || {}), [qIdx]: choice } };
    setQuiz(nextQuiz);
    persist(done, nextQuiz, teach);
  };

  const saveTeach = (mid, value) => {
    const nextTeach = { ...teach, [mid]: value };
    setTeach(nextTeach);
    persist(done, quiz, nextTeach);
  };

  const markComplete = (mod) => {
    const answered = quiz[mod.id] || {};
    const correct = mod.quiz.reduce((n, q, i) => n + (answered[i] === q.correct ? 1 : 0), 0);
    if (correct < Math.ceil(mod.quiz.length * 0.5)) {
      toast.error(t("learn.quiz_gate"));
      return;
    }
    const nextDone = { ...done, [mod.id]: !done[mod.id] };
    setDone(nextDone);
    persist(nextDone, quiz, teach);
    if (nextDone[mod.id]) toast.success(`${t("learn.lesson_complete")}: ${mod.title}`);
  };

  const total = items.length;
  const completed = items.filter((m) => done[m.id]).length;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
          <GraduationCap className="w-6 h-6 text-[#C85A32]" /> {t("learn.title")}
        </h1>
        <p className="text-[#4A525A] mt-1">{t("learn.subtitle")}</p>
      </div>

      <div className="bg-[#FAF5EC] border-2 border-[#E4DDD3] rounded-2xl p-4 flex items-center justify-between" data-testid="learn-progress">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#707880] font-semibold">{t("learn.progress")}</div>
          <div className="text-lg font-bold">
            {loadingItems ? t("learn.loading") : `${completed} ${t("learn.progress_of")} ${total} ${t("learn.progress_complete")}`}
          </div>
          {user && typeof user === "object" && !loadingItems && (
            <div className="text-xs text-[#4A525A] mt-0.5">{t("learn.synced")}</div>
          )}
        </div>
        <div className="flex items-center gap-3">
          {completed >= total && total > 0 && (
            <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-300 text-xs font-bold px-2.5 py-1 rounded-full">
              <Award className="w-3.5 h-3.5" /> {t("learn.academy_complete")}
            </span>
          )}
          <div className="w-32 h-2 rounded-full bg-white overflow-hidden">
            <div className="h-full bg-[#C85A32] transition-all" style={{ width: total ? `${(completed / total) * 100}%` : 0 }} />
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {loadingItems && Array.from({ length: 4 }).map((_, i) => (
          <div key={`skeleton-${i}`} data-testid={`module-skeleton-${i}`} className="bg-white border-2 border-[#E4DDD3] rounded-2xl p-5 animate-pulse">
            <div className="h-3 w-16 bg-[#F3EFE6] rounded mb-3" />
            <div className="h-5 w-2/3 bg-[#F3EFE6] rounded mb-2" />
            <div className="h-4 w-1/2 bg-[#F3EFE6] rounded" />
          </div>
        ))}
        {items.map((m) => (
          <div key={m.id} data-testid={`module-${m.id}`} className="bg-white border-2 border-[#E4DDD3] rounded-2xl overflow-hidden">
            <button
              onClick={() => setOpen(open === m.id ? null : m.id)}
              className="pod-focus w-full text-left p-5 flex items-center justify-between"
              data-testid={`module-toggle-${m.id}`}
            >
              <div>
                <div className="flex items-center gap-2">
                  <div className="text-xs uppercase tracking-widest text-[#707880] font-semibold">{m.duration}</div>
                  {done[m.id] && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
                </div>
                <div className="text-lg font-bold mt-1">{m.title}</div>
                <div className="text-[#4A525A] text-sm mt-1">{m.objective}</div>
              </div>
              <span className="text-[#C85A32] font-bold text-sm inline-flex items-center gap-1">
                {open === m.id ? <>{t("learn.hide")} <ChevronUp className="w-4 h-4" /></> : <>{t("learn.open")} <ChevronDown className="w-4 h-4" /></>}
              </span>
            </button>

            {open === m.id && (
              <div className="border-t border-[#E4DDD3] p-5 bg-[#FBF9F5] space-y-6">
                <Section label={t("learn.section_read")}>
                  <p className="text-[#1C2024] leading-relaxed">{m.summary}</p>
                  {m.key_points && m.key_points.length > 0 && (
                    <ul className="mt-3 space-y-2">
                      {m.key_points.map((kp, i) => (
                        <li key={`${m.id}-kp-${i}-${kp.slice(0, 8)}`} className="flex gap-3">
                          <span className="w-5 h-5 shrink-0 rounded-full bg-[#FAF5EC] text-[#C85A32] font-bold text-xs flex items-center justify-center mt-0.5">{i + 1}</span>
                          <span>{kp}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </Section>

                <Section label={t("learn.section_quiz")}>
                  <div className="space-y-4">
                    {m.quiz.map((q, qi) => {
                      const selected = quiz[m.id]?.[qi];
                      const answered = selected !== undefined;
                      return (
                        <div key={`${m.id}-q-${qi}`} data-testid={`module-${m.id}-quiz-${qi}`} className="bg-white border border-[#E4DDD3] rounded-xl p-4">
                          <div className="font-semibold">{qi + 1}. {q.q}</div>
                          <div className="mt-3 grid grid-cols-1 gap-2">
                            {q.options.map((opt, oi) => {
                              const isSel = selected === oi;
                              const isCorrect = oi === q.correct;
                              let cls = "border-[#E4DDD3] hover:border-[#C85A32] bg-white";
                              if (answered) {
                                if (isCorrect) cls = "border-emerald-500 bg-emerald-50";
                                else if (isSel) cls = "border-red-400 bg-red-50";
                                else cls = "border-[#E4DDD3] bg-white opacity-70";
                              }
                              return (
                                <button
                                  key={`${m.id}-q${qi}-opt-${oi}`}
                                  data-testid={`module-${m.id}-quiz-${qi}-opt-${oi}`}
                                  onClick={() => answerQuiz(m.id, qi, oi)}
                                  className={`pod-focus text-left p-3 rounded-lg border-2 transition-colors ${cls}`}
                                >
                                  <div className="flex items-center gap-2">
                                    {answered && isCorrect && <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />}
                                    {answered && isSel && !isCorrect && <XCircle className="w-4 h-4 text-red-500 shrink-0" />}
                                    <span>{opt}</span>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                          {answered && (
                            <div className="mt-3 text-sm border-l-2 border-[#C85A32]/40 pl-3">
                              <span className="font-semibold">{t("learn.why")} </span>{q.why}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </Section>

                {m.teach_back && (
                  <Section label={t("learn.section_teach")}>
                    <div className="text-[#1C2024]">{m.teach_back}</div>
                    <textarea
                      rows={3}
                      data-testid={`module-${m.id}-teach`}
                      value={teach[m.id] || ""}
                      onChange={(e) => saveTeach(m.id, e.target.value)}
                      placeholder={t("learn.teach_placeholder")}
                      className="mt-2 w-full rounded-xl border-2 border-[#E4DDD3] focus:border-[#C85A32] focus:ring-4 focus:ring-[#C85A32]/20 outline-none p-3 bg-white"
                    />
                  </Section>
                )}

                {m.scenario && (
                  <Section label={t("learn.section_scenario")}>
                    <div className="flex gap-3 items-start">
                      <Sparkles className="w-4 h-4 text-[#C85A32] mt-1 shrink-0" />
                      <div>{m.scenario}</div>
                    </div>
                  </Section>
                )}

                <button
                  onClick={() => markComplete(m)}
                  data-testid={`module-done-${m.id}`}
                  className="pod-focus bg-[#2C4A3E] hover:bg-[#1F382E] text-white font-bold py-2 px-4 rounded-xl inline-flex items-center gap-2"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  {done[m.id] ? t("learn.mark_incomplete") : t("learn.mark_complete")}
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-[#707880] font-semibold mb-2">{label}</div>
      {children}
    </div>
  );
}
